/**
 * An affiliate's Earnings (TMMRSL-0051, MM-D-106) on its detail, after the deal panel: two tables,
 * By status (pending, payable, voided, clawed back, recovered) and By kind (recurring, bounties,
 * milestones), one row per month, newest first, in the earnings' own currency. Every amount is
 * required, so both tables render. A defensive route `404` means the whole section is
 * hidden; any other failure shows the error with Retry inside the section.
 *
 * It loads its own affiliate's earnings (TMMRSL-0053) and is rendered keyed by `affiliateId`, so
 * another affiliate starts clean — never showing the last one's rows. Until the first answer for
 * this affiliate arrives nothing renders (no title, no skeleton, so a `404` never flashes the
 * section); a later reload keeps the rows at full contrast in a `BusyRegion` (progress bar,
 * `aria-busy`).
 */
import { useCallback, useState } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '@/components/ApiErrorState';
import { BusyRegion } from '@/components/BusyRegion';
import { MoneyCell } from '@/components/cells';
import { useRemote } from '@/components/useRemote';
import { formatCivilMonth } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useTenantApi } from '@/tenant/useTenant';
import {
  isEarningsRouteMissing,
  normalizeAffiliateEarnings,
  type AffiliateEarnings as Earnings,
  type EarningsColumn,
} from './affiliateEarnings';

const BY_STATUS: EarningsColumn[] = ['pending', 'payable', 'voided', 'clawedBack', 'recovered'];
const BY_KIND: EarningsColumn[] = ['recurring', 'bounty', 'milestone'];
export interface AffiliateEarningsPanelProps {
  /** Render it with `key={affiliateId}` so each affiliate starts clean. */
  affiliateId: number;
}

export function AffiliateEarningsPanel({ affiliateId }: AffiliateEarningsPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const api = useTenantApi();
  const loadEarnings = useCallback(
    async () => normalizeAffiliateEarnings(await api.getAffiliateEarnings(affiliateId)),
    [api, affiliateId],
  );
  const earnings = useRemote(loadEarnings);
  // Set once this affiliate's first answer (rows or error) is in; nothing renders before it.
  const [answered, setAnswered] = useState(false);
  if (!answered && earnings.status !== 'loading') setAnswered(true);
  const dash = t('affiliates.unavailable');

  if (!answered) return null;
  if (earnings.status === 'error' && isEarningsRouteMissing(earnings.error)) return null;
  const busy = earnings.status === 'loading' && earnings.data !== null;

  function monthLabel(month: string): string {
    try {
      return formatCivilMonth(month, locale);
    } catch {
      return dash;
    }
  }

  function table(data: Earnings, name: string, group: EarningsColumn[]) {
    const columns = group.filter((column) => data.present.has(column));
    if (columns.length === 0) return null;
    const amountOf = (column: EarningsColumn, minor: number | null | undefined) => {
      if (minor == null) return null;
      // Clawed back is taken back from the affiliate: always shown negative.
      if (column === 'clawedBack') return minor === 0 ? 0 : -Math.abs(minor);
      return minor;
    };
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
          {name}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={name}>
            <TableHead>
              <TableRow>
                <TableCell>{t('affiliates.earnings.col.month')}</TableCell>
                {columns.map((column) => (
                  <TableCell key={column} align="right">
                    {t(`affiliates.earnings.col.${column}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.months.map((month) => (
                <TableRow key={month.month}>
                  <TableCell component="th" scope="row">
                    {monthLabel(month.month)}
                  </TableCell>
                  {columns.map((column) => (
                    <MoneyCell
                      key={column}
                      minor={amountOf(column, month.amounts[column])}
                      currency={data.currency}
                      signed={column === 'recovered'}
                    />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  }

  function body() {
    if (earnings.status === 'error') {
      return (
        <Box sx={{ mt: 1.5 }}>
          <ApiErrorState error={earnings.error} onRetry={earnings.reload} />
        </Box>
      );
    }
    const data = earnings.data;
    if (!data) {
      return (
        <Box role="status" aria-label={t('common.loading')} sx={{ mt: 1.5 }}>
          <Skeleton variant="rounded" height={120} />
        </Box>
      );
    }
    if (data.months.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('affiliates.earnings.empty')}
        </Typography>
      );
    }
    return (
      <>
        {table(data, t('affiliates.earnings.byStatus'), BY_STATUS)}
        {table(data, t('affiliates.earnings.byKind'), BY_KIND)}
      </>
    );
  }

  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="h3" component="h3">
        {t('affiliates.earnings.title')}
      </Typography>
      <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 0.5 }}>
        {t('affiliates.earnings.note')}
      </Typography>
      <BusyRegion busy={busy}>{body()}</BusyRegion>
    </Paper>
  );
}
